import * as React from 'react'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import LaunchIcon from '@mui/icons-material/Launch'

import type { ICustomFieldFormSetting } from '@user/store/entityFormSettings/typings'
import type { ENTITY_TYPES } from '@sales-center/common'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { useCustomSettingsSectionHandlers } from '@user/components/Settings/EntityFormSettings/CustomSettings/hooks/useCustomSettingsSectionHandlers'
import { CustomSettingsCards } from '@user/components/Settings/EntityFormSettings/CustomSettings/CustomSettingsCards'
import { ReOrderDrawer } from './ReOrderDrawer'
import { getSectionStyles } from './styling'

interface Props {
  settings: ICustomFieldFormSetting[]
  entityType: ENTITY_TYPES
}

/**
 * The custom fields section: its heading, card list, and reorder drawer.
 *
 * Reads the Tidal theme flag itself so the caller renders one section
 * component regardless of theme; every theme-dependent style comes from
 * `getSectionStyles`. The two remaining branches on the theme are structural:
 * the legacy "manage" button sits inside an extra row wrapper, and the empty
 * state uses an `AlertTitle` under Tidal but a wrapped `Typography` otherwise.
 *
 * Note the render threshold differs by theme, which is long-standing
 * behaviour rather than a deliberate choice: under the Tidal theme the card
 * list and reorder button appear only once there are two or more custom
 * fields, so a lone custom field renders neither a card nor the "no fields"
 * message. Outside that theme any non-zero count renders the card list.
 */
export function CustomSettingsSection(props: Props) {
  const { settings, entityType } = props
  const isTidalThemeActive = useTidalThemeActive()
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false)
  const { onClose, onOpen } = useCustomSettingsSectionHandlers({
    entityType,
    setDrawerOpen,
  })
  const navigate = useNavigate()
  const theme = useTheme()
  const styles = getSectionStyles(theme, isTidalThemeActive)

  const redirectToCustomFields = useCallback(() => {
    navigate('/settings/customFields')
  }, [navigate])

  const showCards = isTidalThemeActive ? settings.length > 1 : !!settings.length

  const manageFieldsButton = (
    <Button
      color="primary"
      variant="text"
      onClick={redirectToCustomFields}
      disableElevation={!isTidalThemeActive}
      sx={styles.manageFieldsButton}
    >
      {styles.props.manageFieldsLabel}
      <LaunchIcon sx={styles.launchIcon} />
    </Button>
  )

  return (
    <Box sx={styles.container}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={styles.headerRow}
      >
        <Typography variant={styles.props.headingVariant}>
          Custom Fields
        </Typography>
        {isTidalThemeActive ? (
          manageFieldsButton
        ) : (
          <Stack direction="row" gap={1.6}>
            {manageFieldsButton}
          </Stack>
        )}
      </Stack>
      {settings.length === 0 &&
        (isTidalThemeActive ? (
          <Alert severity="info">
            <AlertTitle>No Custom Fields set up</AlertTitle>
          </Alert>
        ) : (
          <Stack direction="row" borderRadius="4px" spacing={1.5} mb={3}>
            <Alert severity="info" sx={{ width: '100%' }}>
              <Typography variant="body2">No Custom Fields set up</Typography>
            </Alert>
          </Stack>
        ))}
      {settings.length > 1 && (
        <Button
          color="primary"
          variant="text"
          onClick={onOpen}
          disableElevation={!isTidalThemeActive}
          sx={styles.reorderButton}
        >
          Reorder custom fields
        </Button>
      )}
      {showCards && (
        <CustomSettingsCards
          entityType={entityType}
          settings={settings}
          isTidalThemeActive={isTidalThemeActive}
        />
      )}
      <ReOrderDrawer
        open={drawerOpen}
        settings={settings}
        onClose={onClose}
        entityType={entityType}
      ></ReOrderDrawer>
    </Box>
  )
}
